import React, { FC, memo, useEffect, useState } from 'react';
import { DefaultLayout } from '@shared/components/DefaultLayout';
import { Outlet } from 'react-router';
import { Role } from '@shared/enums/role';
import { useLocation } from 'react-router-dom';
import { RocketLoader } from '../RocketLoader';
import { useStyles } from './AuthProtectedRoute.styles';
import { useAuth, useNavigateToUnauthorized } from '@shared/hooks/useAuth';
import { AccessDeniedView } from '../DataErrorView/components/AccessDeniedView';
import * as Sentry from '@sentry/react';
import { ErrorView } from '@modules/App/pages/ErrorView';
import { TERMS_OF_USE_VERSION } from '../TermsOfUse/consntants';
import { TermsOfUseOverlay } from '../TermsOfUse/TermsOfUseOverlay';
import { useDispatch, useSelector } from 'react-redux';
import { acceptTermsOfUse, trackEvent } from '@modules/App/redux/user/actions';
import { RockyComponentsProvider } from '@product-studio/rocky-components';
import { Enviroment } from '@shared/enums/EnvironmentNames';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { DataState } from '@shared/enums/DataState';
import { authUserErrorSelector, authUserState } from '@modules/App/redux/user/selectors';
import { firebaseAuth } from '../../../auth/firebase';

interface Props {
  allowedRoles?: Role[];
  checkIfUserHasReporters?: boolean;
  isAvailableForManagers?: boolean;
  isCheckHappinessAvailabilityInCurrentUser?: boolean;
  isCheckEvaluationsAvailabilityInCurrentUser?: boolean;
}

const AuthProtectedRouteComponent: FC<Props> = ({
  allowedRoles: roles,
  checkIfUserHasReporters,
  isAvailableForManagers = false,
  isCheckHappinessAvailabilityInCurrentUser = false,
  isCheckEvaluationsAvailabilityInCurrentUser = false,
}) => {
  const styles = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const acceptTou = () => dispatch(acceptTermsOfUse(TERMS_OF_USE_VERSION));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const { currentUser } = useAuth();

  const isTouAccepted = currentUser?.latestTermsOfUseVersionAccepted === TERMS_OF_USE_VERSION;
  const isAllowed = isCheckHappinessAvailabilityInCurrentUser
    ? currentUser?.hasAvailableHappinessExceptOwn
    : isCheckEvaluationsAvailabilityInCurrentUser
    ? currentUser?.hasAvailableEvaluationsExceptOwn
    : !roles ||
      (currentUser && currentUser.rockyRoles.some((r) => roles.includes(r))) ||
      (checkIfUserHasReporters && currentUser?.hasReporters);

  const currentUserState = useSelector(authUserState);
  const currentUserError = useSelector(authUserErrorSelector);

  const [authToken, setAuthToken] = useState<string | null>(null);

  useEffect(() => {
    if (!firebaseAuth) return;
    const unsubscribe = onIdTokenChanged(firebaseAuth, async (user) => {
      if (user) {
        const idToken = await user.getIdToken();
        setAuthToken(idToken);
      } else {
        setAuthToken(null);
      }
    });
    return unsubscribe;
  }, []);
  const navigateToUnauthorized = useNavigateToUnauthorized();

  useEffect(() => {
    if (!firebaseAuth) return;
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (userCred) => {
      if (userCred && currentUserState === DataState.Rejected) {
        navigateToUnauthorized(userCred.email || '', currentUserError);
        return;
      }
    });
    return unsubscribe;
  }, [currentUserState, currentUserError]);

  useEffect(() => {
    if (currentUser) {
      Sentry.setUser({ email: currentUser?.email });
    }
  }, [currentUser]);

  useEffect(() => {
    dispatch(
      trackEvent({
        actionType: 'PAGE_VISIT',
        data: { route: location.pathname, utm_source: new URLSearchParams(location.search).get('utm_source') },
      })
    );
  }, [location.pathname, location.search]);

  return currentUser && authToken ? (
    isTouAccepted ? (
      <RockyComponentsProvider
        token={authToken}
        env={
          process.env.REACT_APP_ENV === Enviroment.Production
            ? 'prod'
            : process.env.REACT_APP_ENV === Enviroment.Develop || process.env.REACT_APP_ENV === Enviroment.Staging
            ? 'dev'
            : 'local'
        }
      >
        <DefaultLayout currentUser={currentUser}>
          {!isAllowed || (isAvailableForManagers && !currentUser.hasReporters) ? (
            <div className={styles.container}>
              <AccessDeniedView />
            </div>
          ) : (
            <Sentry.ErrorBoundary fallback={<ErrorView component />}>
              <Outlet></Outlet>
            </Sentry.ErrorBoundary>
          )}
        </DefaultLayout>
      </RockyComponentsProvider>
    ) : (
      <TermsOfUseOverlay onTermsAccept={acceptTou}></TermsOfUseOverlay>
    )
  ) : (
    <div className={styles.loaderHolder}>
      <RocketLoader></RocketLoader>
      <h3 className={styles.loadingText}>Launching space mission</h3>
    </div>
  );
};

export const AuthProtectedRoute = memo(AuthProtectedRouteComponent);
